
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Check, ShieldCheck, Clock, PiggyBank, Info } from "lucide-react";
import EnhancedSeoMetadataComponent from "@/components/common/EnhancedSeoMetadata";
import { getEnhancedSeoDataForRoute } from "@/utils/enhancedSeoUtils";

const SwedishPage = () => {
  const seoData = getEnhancedSeoDataForRoute("/sv");
  
  return (
    <>
      <EnhancedSeoMetadataComponent
        title="Lainasampo.fi | Jämför lån och spara pengar"
        description="Lainasampo.fi hjälper dig att hitta det mest prisvärda lånet. Jämför bolån, konsumentkrediter eller konsolideringslån enkelt med en ansökan."
        keywords={["lånematching", "lånförfrågan", "låneförmedling", "lån", "bolån", "konsumentkredit", "finland"]}
        canonicalUrl="https://lainasampo.fi/sv"
        ogTitle="Lainasampo.fi | Jämför lån och spara pengar"
        ogDescription="Lainasampo.fi hjälper dig att hitta det mest prisvärda lånet. Jämför bolån, konsumentkrediter eller konsolideringslån enkelt med en ansökan."
        ogImage="/lainasampo-social-card.jpg"
      />

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-blue-50 to-white py-16 md:py-24">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <h1 className="text-4xl md:text-5xl font-bold text-blue-700 mb-6">
                  Jämför lån och spara pengar
                </h1>
                <p className="text-xl text-gray-700 mb-8">
                  Få de bästa lånerbjudandena från flera banker med en enda ansökan.
                </p>
                <Link to="/sv/apply">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-md shadow-lg hover:shadow-xl transition-all">
                    Jämför lånerbjudanden nu
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        {/* About Finnish Loans Section */}
        <section className="py-12 md:py-16 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
                Förstå lån i Finland
              </h2>
              
              <div className="bg-blue-50 p-6 rounded-xl mb-8">
                <div className="flex items-start mb-4">
                  <Info className="h-6 w-6 text-blue-600 mr-3 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">
                    Den finska lånemarknaden erbjuder konkurrenskraftiga räntor och transparenta villkor. Oavsett om du söker 
                    konsumentlån, bolån eller företagsfinansiering ger Finlands välreglerade banksystem säkerhet och tillförlitlighet.
                  </p>
                </div>
                
                <h3 className="text-xl font-medium text-gray-800 mb-3">Viktiga egenskaper hos finska lån:</h3>
                <ul className="space-y-2 mb-4">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Fasta räntor för de flesta konsumentlån, vilket ger betalningsstabilitet</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Inget behov av fysiska bankbesök - helt digital ansökningsprocess</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Grundliga kreditkontroller genom Suomen Asiakastieto för att säkerställa ansvarsfull utlåning</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Flexibla återbetalningsalternativ inklusive möjligheter till förtida återbetalning</span>
                  </li>
                </ul>
                
                <p className="text-gray-700">
                  De flesta lån i Finland kräver ett finskt personnummer (henkilötunnus) och åtminstone ett 
                  tillfälligt uppehållstillstånd. Banker tar hänsyn till din inkomst, anställningsstatus och 
                  kredithistorik när de behandlar din ansökan.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-12 md:py-20 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
                Varför välja Lainasampo.fi?
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <ShieldCheck className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Gratis jämförelse</h3>
                  <p className="text-gray-600">
                    Jämför lånerbjudanden från flera banker samtidigt, helt kostnadsfritt.
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <Clock className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Snabbt svar</h3>
                  <p className="text-gray-600">
                    Få lånerbjudanden inom 24 timmar. Inget pappersarbete eller bankbesök krävs.
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <PiggyBank className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Spara pengar</h3>
                  <p className="text-gray-600">
                    Hitta de bästa räntorna och spara tusentals euro under lånets löptid.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-16 bg-blue-600 text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Redo att hitta det bästa lånet?
              </h2>
              <p className="text-xl mb-8 text-blue-100">
                Fyll i en ansökan så hittar vi de bästa erbjudandena åt dig.
              </p>
              <Link to="/sv/apply">
                <Button size="lg" className="bg-white hover:bg-gray-100 text-blue-600 px-8 py-6 text-lg rounded-md shadow-lg hover:shadow-xl transition-all">
                  Starta min ansökan
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* How it works section */}
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
                Hur det fungerar
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-blue-600 text-white h-8 w-8 rounded-full flex items-center justify-center font-bold mr-4">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      Fyll i en ansökan
                    </h3>
                    <p className="text-gray-600">
                      Fyll i ett enda låneansökningsformulär med dina uppgifter och preferenser.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-blue-600 text-white h-8 w-8 rounded-full flex items-center justify-center font-bold mr-4">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      Få flera erbjudanden
                    </h3>
                    <p className="text-gray-600">
                      Vi skickar din ansökan till våra partnerbanker som ger dig personliga erbjudanden.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 bg-blue-600 text-white h-8 w-8 rounded-full flex items-center justify-center font-bold mr-4">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      Välj det bästa erbjudandet
                    </h3>
                    <p className="text-gray-600">
                      Jämför erbjudandena och välj det som bäst passar dina behov. Ingen skyldighet att acceptera.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-12 text-center">
                <Link to="/sv/apply">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                    Jämför lån nu
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default SwedishPage;
